const BASE_URL = '/api';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    ...options,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Request failed' }));
    throw new ApiError(res.status, (body as { error?: string }).error || 'Request failed');
  }

  // Handle 204 No Content
  if (res.status === 204) {
    return undefined as T;
  }

  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Types – mirror the server models
// ---------------------------------------------------------------------------

export interface Host {
  id: string;
  email: string;
  name: string;
  plan: 'FREE' | 'PRO' | 'ENTERPRISE';
  isPlatformAdmin?: boolean;
}

export interface HostWithStats {
  id: string;
  email: string;
  name: string;
  plan: string;
  isPlatformAdmin: boolean;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  eventsCount: number;
  activeEventsCount: number;
  totalGuestsCount: number;
  totalRevenueCents: number;
}

export interface HostDetail extends HostWithStats {
  events: Array<{
    id: string;
    title: string;
    dateTime: string;
    status: string;
    guestCount: number;
    rsvpYes: number;
    rsvpNo: number;
    rsvpMaybe: number;
    billingCents: number;
    billingStatus: string;
  }>;
}

export interface PlatformStats {
  totalHosts: number;
  activeHosts: number;
  totalEvents: number;
  activeEvents: number;
  totalGuests: number;
  totalRevenueCents: number;
  recentSignups: Array<{ id: string; name: string; email: string; createdAt: string }>;
}

export interface Template {
  id: string;
  name: string;
  category: string;
  previewImageUrl: string;
  isDefault: boolean;
}

export interface AdminTemplate extends Template {
  htmlLayout: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface FeatureFlag {
  id: string;
  name: string;
  description: string | null;
  isEnabled: boolean;
  targetPlans: string[] | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface AbuseReport {
  id: string;
  hostId: string | null;
  eventId: string | null;
  reportType: string;
  description: string | null;
  status: string;
  resolvedBy: string | null;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EmailHealthSummary {
  totalSent: number;
  totalDelivered: number;
  totalBounced: number;
  totalFailed: number;
  totalOpened: number;
  totalClicked: number;
  totalComplaints: number;
  deliveryRate: number;
  bounceRate: number;
  complaintRate: number;
  openRate: number;
  clickRate: number;
}

export interface EmailHealthMetric {
  id: string;
  date: string;
  totalSent: number;
  totalDelivered: number;
  totalBounced: number;
  totalFailed: number;
  totalOpened: number;
  totalClicked: number;
  totalComplaints: number;
}

export interface EmailHealthResponse {
  summary: EmailHealthSummary;
  history: EmailHealthMetric[];
}

export interface Event {
  id: string;
  title: string;
  description?: string;
  dateTime: string;
  endDateTime?: string;
  location?: string;
  locationUrl?: string;
  timezone: string;
  templateId: string;
  status: string;
  billingEstimate?: { guestCount: number; priceCents: number };
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  totalPages: number;
}

export interface GuestBulkResult {
  created: Array<{ id: string; email: string; name: string }>;
  skipped: string[];
  billingEstimate: { guestCount: number; priceCents: number } | null;
}

export interface Guest {
  id: string;
  email: string;
  name: string;
  notes?: string;
  plusOneName?: string;
  source?: string;
  rsvpStatus: string;
  rsvpAt?: string;
  waitlistPosition?: number | null;
  waitlistedAt?: string | null;
  promotedAt?: string | null;
  checkedInAt?: string | null;
  checkInMethod?: string | null;
  qrCode?: string | null;
}

export interface WaitlistGuest {
  id: string;
  email: string;
  name: string;
  waitlistPosition: number;
  waitlistedAt: string;
}

export interface CheckInStats {
  total: number;
  checkedIn: number;
  notCheckedIn: number;
  checkedInPercent: number;
}

export interface CheckInGuest {
  id: string;
  email: string;
  name: string;
  checkedInAt: string | null;
  checkInMethod: string | null;
  qrCode: string | null;
  rsvp: {
    response: string;
    plusOnesCount: number;
  } | null;
}

export interface GuestGroup {
  id: string;
  name: string;
  memberCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface GuestGroupMember {
  id: string;
  email: string;
  name: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface GuestGroupDetail {
  id: string;
  name: string;
  hostId: string;
  createdAt: string;
  updatedAt: string;
  members: GuestGroupMember[];
}

export interface GroupMembersResult {
  created: GuestGroupMember[];
  skipped: string[];
}

export interface CsvImportResult {
  created: Array<{ id: string; email: string; name: string }>;
  skipped: string[];
  invalid: Array<{ row: number; data: string; reason: string }>;
  billingEstimate: { guestCount: number; priceCents: number } | null;
}

export interface EventDashboard {
  event: {
    id: string;
    title: string;
    dateTime: string;
    endDateTime: string | null;
    location: string | null;
    timezone: string;
    status: string;
    description: string | null;
    templateName: string;
  };
  rsvpSummary: { yes: number; no: number; maybe: number; pending: number };
  guests: Array<{
    id: string;
    name: string;
    email: string;
    rsvpStatus: 'YES' | 'NO' | 'MAYBE' | 'PENDING';
    rsvpDate: string | null;
    emailOpened: boolean;
    emailClicked: boolean;
    emailBounced: boolean;
    emailSentAt: string | null;
  }>;
  emailStats: {
    sent: number;
    delivered: number;
    opened: number;
    clicked: number;
    bounced: number;
    failed: number;
  };
}

// Privacy / GDPR types
export interface DeletionRequestResult {
  id: string;
  status: string;
  message: string;
}

export interface DeletionRequestStatus {
  id: string;
  requestType: string;
  status: string;
  processedAt: string | null;
  createdAt: string;
}

export interface DataExportResult {
  id: string;
  status: string;
  message: string;
}

export interface DataRetentionPolicy {
  id: string;
  hostId: string;
  retainGuestDataDays: number;
  autoDeleteEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DeletionRequestAdmin {
  id: string;
  email: string;
  requestType: string;
  status: string;
  processedAt: string | null;
  exportUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProcessDeletionsResult {
  processed: number;
  results: Array<{ id: string; status: string }>;
}

export interface EventAnalytics {
  emailMetrics: {
    totalSent: number;
    delivered: number;
    opened: number;
    clicked: number;
    bounced: number;
    failed: number;
    openRate: number;
    clickRate: number;
    bounceRate: number;
  };
  rsvpMetrics: {
    totalGuests: number;
    responded: number;
    yes: number;
    no: number;
    maybe: number;
    pending: number;
    responseRate: number;
    conversionRate: number;
  };
  timeToRsvp: {
    averageHours: number;
    medianHours: number;
    fastestHours: number;
    slowestHours: number;
  };
  rsvpTimeline: Array<{
    date: string;
    cumulative: number;
    daily: number;
  }>;
  emailTimeline: Array<{
    date: string;
    opens: number;
    clicks: number;
  }>;
}

export interface PlatformAnalyticsData {
  events: { total: number; active: number; completed: number; cancelled: number };
  emails: {
    totalSent: number;
    deliveryRate: number;
    openRate: number;
    clickRate: number;
    bounceRate: number;
  };
  rsvps: { totalResponses: number; averageConversionRate: number };
  referrals: { totalClicks: number; bySource: Record<string, number> };
  growth: Array<{ date: string; newHosts: number; newEvents: number }>;
}

export interface SendInvitesResult {
  message: string;
  sent: number;
  failed: number;
  failedGuests: string[];
}

export interface ScheduledEmail {
  id: string;
  eventId: string;
  emailType: string;
  scheduledFor: string;
  status: string;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface ScheduleEmailResult {
  message: string;
  scheduledEmail: { id: string; scheduledFor: string };
}

export interface EmailActionResult {
  message: string;
  sent: number;
  failed: number;
}

export interface UnsubscribeInfo {
  email: string;
  eventTitle: string;
  eventId: string;
  alreadyUnsubscribed: boolean;
}

export interface UnsubscribeResult {
  message: string;
  email: string;
  eventTitle: string;
}

export interface RsvpQuestion {
  id: string;
  question: string;
  type: 'text' | 'select' | 'multiselect';
  options: string[] | null;
  required: boolean;
  sortOrder: number;
}

export interface RsvpQuestionWithAnswers extends RsvpQuestion {
  answers: Array<{ guestId: string; answer: string; createdAt: string }>;
  _count: { answers: number };
}

export interface AnswersByQuestion {
  questionId: string;
  question: string;
  type: string;
  options: unknown;
  answers: Array<{ guestId: string; answer: string; createdAt: string }>;
}

export interface CalendarLinks {
  google: string;
  outlook: string;
  ics: string;
}

export interface PublicEventPageData {
  event: {
    id: string;
    title: string;
    description?: string | null;
    descriptionHtml?: string | null;
    dateTime: string;
    endDateTime?: string | null;
    location?: string | null;
    locationUrl?: string | null;
    timezone: string;
    imageUrl?: string | null;
    bannerImageUrl?: string | null;
    virtualEventUrl?: string | null;
    virtualEventType?: string | null;
    mapEmbedUrl?: string | null;
    showGuestList: boolean;
    slug?: string | null;
    status: string;
  };
  faqs: Array<{
    id: string;
    question: string;
    answer: string;
    sortOrder: number;
  }>;
  rsvpSummary: { yes: number; no: number; maybe: number };
  attendingGuests: string[];
}

export interface EventPageSettings {
  id: string;
  title: string;
  descriptionHtml?: string | null;
  bannerImageUrl?: string | null;
  virtualEventUrl?: string | null;
  virtualEventType?: string | null;
  mapEmbedUrl?: string | null;
  showGuestList: boolean;
  slug?: string | null;
}

export interface RsvpEventData {
  event: {
    title: string;
    dateTime: string;
    endDateTime?: string;
    location?: string;
    locationUrl?: string;
    timezone: string;
    description?: string;
    status: string;
    template: { name: string };
    faqs: Array<{ id: string; question: string; answer: string; sortOrder: number }>;
  };
  guest: {
    name: string;
    plusOnesMax: number;
  };
  rsvp: {
    response: string;
    plusOnesCount: number;
    dietaryNotes?: string;
    message?: string;
    respondedAt: string;
  } | null;
  rsvpQuestions?: RsvpQuestion[];
  calendarLinks?: CalendarLinks;
}

export interface HostProfileData {
  id: string;
  email: string;
  name: string;
  plan: string;
  isPlatformAdmin: boolean;
  isActive: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  stats: {
    totalEvents: number;
    totalGuests: number;
    totalRevenueCents: number;
    totalEmailsSent: number;
  };
}

export interface EmailUsageEvent {
  eventId: string;
  eventTitle: string;
  eventDate: string;
  guestCount: number;
  emailsSent: number;
  emailsIncluded: number;
  overageCount: number;
  overageCostCents: number;
}

export interface EmailUsageSummary {
  totalEmailsSent: number;
  totalEmailsIncluded: number;
  totalOverageCount: number;
  totalOverageCostCents: number;
  events: EmailUsageEvent[];
}

export interface NotificationPrefsData {
  id: string;
  hostId: string;
  emailOnRsvp: boolean;
  emailOnReminder: boolean;
  emailOnBilling: boolean;
  emailDigest: string;
}

export interface BillingRecord {
  id: string;
  eventId: string;
  eventTitle: string;
  eventDate: string;
  guestCount: number;
  basePriceCents: number;
  overageGuestCents: number;
  emailOverageCount: number;
  emailOverageCents: number;
  totalCents: number;
  status: string;
  paidAt: string | null;
  createdAt: string;
}

export interface CheckoutResult {
  url: string | null;
  free?: boolean;
  billing?: EventBillingDetail;
}

export interface PromoValidation {
  valid: boolean;
  code: string;
  discountType: 'percent' | 'fixed';
  discountValue: number;
}

export interface EventBillingDetail {
  id: string;
  eventId: string;
  guestCount: number;
  basePriceCents: number;
  overageGuestCents: number;
  emailOverageCount: number;
  emailOverageCents: number;
  totalCents: number;
  status: 'PENDING' | 'PAID' | 'FAILED' | 'REFUNDED';
  stripePaymentId: string | null;
  paidAt: string | null;
  promoCodeId: string | null;
  discountCents: number;
  receiptUrl: string | null;
  lineItems?: Array<{
    id: string;
    description: string;
    quantity: number;
    unitPriceCents: number;
    totalCents: number;
  }>;
  promoCode?: {
    id: string;
    code: string;
    discountType: string;
    discountValue: number;
  } | null;
}

export interface PromoCodeItem {
  id: string;
  code: string;
  discountType: 'percent' | 'fixed';
  discountValue: number;
  maxUses: number | null;
  usedCount: number;
  expiresAt: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: { billings: number };
}

export interface CreatePromoCodeData {
  code: string;
  discountType: 'percent' | 'fixed';
  discountValue: number;
  maxUses?: number | null;
  expiresAt?: string | null;
}

// Guest List Tab & Food Contribution types
export interface GuestListTabData {
  mode: 'invited' | 'confirmed' | 'stats_only';
  guests?: Array<{ name: string }>;
  stats?: { invited: number; confirmed: number; maybe: number; declined: number };
}

export interface FoodInfoData {
  foodMode: 'potluck' | 'bring_a_dish';
  stats: Record<string, number>;
  contributions: Array<{
    guestName: string;
    category?: string;
    dishName?: string;
    description?: string;
    recipeUrl?: string;
    allergens?: string[];
  }>;
}

export interface GuestListAnalytics {
  totalViews: number;
  uniqueViewers: number;
  viewedThenRsvped: number;
  rsvpedThenViewed: number;
  conversionRate: number;
}

export interface FoodContribution {
  id: string;
  guestName: string;
  guestEmail: string;
  category?: string;
  dishName?: string;
  description?: string;
  recipeUrl?: string;
  allergens?: string[];
}

export interface FoodStats {
  foodMode: 'potluck' | 'bring_a_dish';
  totalContributions: number;
  categoryCounts?: Record<string, number>;
  allergenCounts?: Record<string, number>;
}

// Gift Exchange types
export interface GiftExchangeParticipantsData {
  participants: Array<{
    guestId: string;
    name: string;
    email: string;
    rsvpResponse: string;
    giftExchangeOptIn: boolean;
    giftExchangeBio: string | null;
  }>;
  participantCount: number;
  canReveal: boolean;
  canRevealReason?: string;
}

export interface GiftExchangeOverviewData {
  assignments: Array<{
    giverGuestId: string;
    giverName: string;
    giverEmail: string;
    receiverGuestId: string;
    receiverName: string;
    receiverEmail: string;
    receiverBio: string | null;
    notifiedAt: string | null;
    viewedAt: string | null;
  }>;
  stats: {
    total: number;
    notified: number;
    viewed: number;
    notViewed: number;
  };
}

export interface GiftAssignmentData {
  revealed: boolean;
  assigned?: boolean;
  receiverName?: string;
  receiverBio?: string | null;
  budget?: number | null;
  biosEnabled?: boolean;
  message?: string;
}

export interface GiftExchangeSettingsData {
  id: string;
  giftExchangeEnabled: boolean;
  giftExchangeBudget: number | null;
  giftExchangeOptOuts: boolean;
  giftExchangeBios: boolean;
  giftExchangeRevealAt: string | null;
  giftExchangeRevealed: boolean;
  suggestedBudgetCents: number | null;
}

export interface GiftExchangeGenerateResult {
  message: string;
  assignments: Array<{
    giverGuestId: string;
    giverName: string;
    receiverGuestId: string;
    receiverName: string;
  }>;
}

export interface GiftExchangeEmailResult {
  message: string;
  sent: number;
  failed: number;
}

// Pro / Enterprise feature types
export interface Branding {
  hostId: string;
  logoUrl: string | null;
  primaryColor: string | null;
  accentColor: string | null;
  fontFamily: string | null;
  customDomain: string | null;
  whiteLabel: boolean;
}

export interface ApiKeyData {
  id: string;
  name: string;
  keyPrefix: string;
  permissions: string[];
  lastUsedAt: string | null;
  expiresAt: string | null;
  isActive: boolean;
  createdAt: string;
}

export interface ApiKeyCreateResult {
  id: string;
  name: string;
  keyPrefix: string;
  permissions: string[];
  plaintextKey: string;
  createdAt: string;
  expiresAt: string | null;
}

export interface HostImageData {
  id: string;
  hostId: string;
  filename: string;
  url: string;
  sizeBytes: number;
  mimeType: string;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Typed API methods
// ---------------------------------------------------------------------------

export const api = {
  auth: {
    register: (data: { email: string; name: string; password: string }) =>
      request<Host>('/auth/register', { method: 'POST', body: JSON.stringify(data) }),

    login: (data: { email: string; password: string }) =>
      request<Host>('/auth/login', { method: 'POST', body: JSON.stringify(data) }),

    logout: () =>
      request<{ success: boolean }>('/auth/logout', { method: 'POST' }),

    me: () => request<Host>('/auth/me'),
  },

  templates: {
    list: () => request<Template[]>('/templates'),
  },

  events: {
    create: (data: Omit<Event, 'id' | 'status' | 'billingEstimate'>) =>
      request<Event>('/events', { method: 'POST', body: JSON.stringify(data) }),

    list: (page = 1, limit = 20) =>
      request<PaginatedResponse<Event>>(`/events?page=${page}&limit=${limit}`),

    get: (eventId: string) => request<Event>(`/events/${eventId}`),

    update: (eventId: string, data: Partial<Omit<Event, 'id'>>) =>
      request<Event>(`/events/${eventId}`, { method: 'PUT', body: JSON.stringify(data) }),

    delete: (eventId: string) =>
      request<void>(`/events/${eventId}`, { method: 'DELETE' }),

    getGuestListAnalytics: (eventId: string) =>
      request<GuestListAnalytics>(`/events/${eventId}/guest-list-analytics`),

    getFoodContributions: (eventId: string) =>
      request<FoodContribution[]>(`/events/${eventId}/food-contributions`),

    getFoodStats: (eventId: string) =>
      request<FoodStats>(`/events/${eventId}/food-stats`),
  },

  giftExchange: {
    getSettings: (eventId: string) =>
      request<GiftExchangeSettingsData>(`/events/${eventId}/gift-exchange/settings`),

    updateSettings: (eventId: string, data: Partial<Omit<GiftExchangeSettingsData, 'id' | 'giftExchangeRevealed'>>) =>
      request<GiftExchangeSettingsData>(`/events/${eventId}/gift-exchange/settings`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    generate: (eventId: string) =>
      request<GiftExchangeGenerateResult>(`/events/${eventId}/gift-exchange/generate`, { method: 'POST' }),

    getOverview: (eventId: string) =>
      request<GiftExchangeOverviewData>(`/events/${eventId}/gift-exchange/overview`),

    getParticipants: (eventId: string) =>
      request<GiftExchangeParticipantsData>(`/events/${eventId}/gift-exchange/participants`),

    sendEmails: (eventId: string) =>
      request<GiftExchangeEmailResult>(`/events/${eventId}/gift-exchange/send-emails`, { method: 'POST' }),
  },

  guests: {
    add: (eventId: string, guests: Array<{ email: string; name: string }>) =>
      request<GuestBulkResult>(`/events/${eventId}/guests`, {
        method: 'POST',
        body: JSON.stringify({ guests }),
      }),

    list: (eventId: string) => request<Guest[]>(`/events/${eventId}/guests`),

    remove: (eventId: string, guestId: string) =>
      request<void>(`/events/${eventId}/guests/${guestId}`, { method: 'DELETE' }),
  },

  dashboard: {
    get: (eventId: string) =>
      request<EventDashboard>(`/events/${eventId}/dashboard`),
  },

  email: {
    sendInvites: (eventId: string) =>
      request<SendInvitesResult>(`/events/${eventId}/send-invites`, { method: 'POST' }),

    scheduleReminder: (eventId: string, daysBefore: number) =>
      request<ScheduleEmailResult>(`/events/${eventId}/schedule-reminder`, {
        method: 'POST',
        body: JSON.stringify({ daysBefore }),
      }),

    scheduleSend: (eventId: string, sendAt: string, emailType: string) =>
      request<ScheduleEmailResult>(`/events/${eventId}/schedule-send`, {
        method: 'POST',
        body: JSON.stringify({ sendAt, emailType }),
      }),

    getScheduledEmails: (eventId: string) =>
      request<{ scheduledEmails: ScheduledEmail[] }>(`/events/${eventId}/scheduled-emails`),

    cancelScheduledEmail: (scheduledId: string) =>
      request<{ message: string }>(`/scheduled-emails/${scheduledId}`, { method: 'DELETE' }),

    sendCancellation: (eventId: string) =>
      request<EmailActionResult>(`/events/${eventId}/send-cancellation`, { method: 'POST' }),

    sendThankYou: (eventId: string) =>
      request<EmailActionResult>(`/events/${eventId}/send-thank-you`, { method: 'POST' }),

    sendUpdate: (eventId: string, message: string) =>
      request<EmailActionResult>(`/events/${eventId}/send-update`, {
        method: 'POST',
        body: JSON.stringify({ message }),
      }),
  },

  unsubscribe: {
    getInfo: (token: string) =>
      request<UnsubscribeInfo>(`/unsubscribe/${token}`),

    confirm: (token: string, reason?: string) =>
      request<UnsubscribeResult>(`/unsubscribe/${token}`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
  },

  rsvp: {
    getEvent: (token: string) =>
      request<RsvpEventData>(`/rsvp/${token}`),

    submit: (data: {
      token: string;
      response: string;
      plusOnesCount?: number;
      dietaryNotes?: string;
      message?: string;
      answers?: Array<{ questionId: string; answer: string }>;
    }) =>
      request<{
        rsvp: { response: string; plusOnesCount: number; respondedAt: string };
        calendarUrl?: string;
        calendarLinks?: CalendarLinks;
      }>('/rsvp', { method: 'POST', body: JSON.stringify(data) }),

    getGuestList: (token: string) =>
      request<GuestListTabData>(`/rsvp/${token}/guest-list`),

    getFoodInfo: (token: string) =>
      request<FoodInfoData>(`/rsvp/${token}/food-info`),

    getGiftAssignment: (token: string) =>
      request<GiftAssignmentData>(`/rsvp/${token}/gift-assignment`),

    recordGuestListView: (token: string) =>
      request<void>(`/rsvp/${token}/guest-list/view`, { method: 'POST' }),
  },

  rsvpQuestions: {
    list: (eventId: string) =>
      request<RsvpQuestionWithAnswers[]>(`/events/${eventId}/rsvp-questions`),

    create: (
      eventId: string,
      data: { question: string; type?: string; options?: string[]; required?: boolean; sortOrder?: number },
    ) =>
      request<RsvpQuestion>(`/events/${eventId}/rsvp-questions`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (
      questionId: string,
      data: {
        question?: string;
        type?: string;
        options?: string[] | null;
        required?: boolean;
        sortOrder?: number;
      },
    ) =>
      request<RsvpQuestion>(`/rsvp-questions/${questionId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: (questionId: string) =>
      request<void>(`/rsvp-questions/${questionId}`, { method: 'DELETE' }),

    getAnswers: (eventId: string) =>
      request<AnswersByQuestion[]>(`/events/${eventId}/rsvp-answers`),
  },

  guestGroups: {
    create: (name: string) =>
      request<GuestGroup>('/guest-groups', {
        method: 'POST',
        body: JSON.stringify({ name }),
      }),

    list: () =>
      request<{ groups: GuestGroup[] }>('/guest-groups'),

    get: (groupId: string) =>
      request<GuestGroupDetail>(`/guest-groups/${groupId}`),

    addMembers: (groupId: string, members: Array<{ email: string; name: string; notes?: string }>) =>
      request<GroupMembersResult>(`/guest-groups/${groupId}/members`, {
        method: 'POST',
        body: JSON.stringify({ members }),
      }),

    removeMember: (groupId: string, memberId: string) =>
      request<void>(`/guest-groups/${groupId}/members/${memberId}`, { method: 'DELETE' }),

    delete: (groupId: string) =>
      request<void>(`/guest-groups/${groupId}`, { method: 'DELETE' }),

    importToEvent: (eventId: string, groupId: string) =>
      request<GuestBulkResult>(`/events/${eventId}/import-group/${groupId}`, { method: 'POST' }),
  },

  csvImport: {
    import: (eventId: string, csvContent: string) =>
      request<CsvImportResult>(`/events/${eventId}/guests/csv`, {
        method: 'POST',
        body: JSON.stringify({ csvContent }),
      }),
  },

  waitlist: {
    list: (eventId: string) =>
      request<{ waitlist: WaitlistGuest[] }>(`/events/${eventId}/waitlist`),

    add: (eventId: string, guestId: string) =>
      request<{ guest: WaitlistGuest }>(`/events/${eventId}/waitlist/${guestId}`, { method: 'POST' }),

    remove: (eventId: string, guestId: string) =>
      request<{ message: string }>(`/events/${eventId}/waitlist/${guestId}`, { method: 'DELETE' }),

    promote: (eventId: string) =>
      request<{ guest: WaitlistGuest }>(`/events/${eventId}/waitlist/promote`, { method: 'POST' }),
  },

  checkin: {
    generateQr: (eventId: string) =>
      request<{ generated: number }>(`/events/${eventId}/generate-qr`, { method: 'POST' }),

    qrCheckIn: (eventId: string, qrCode: string) =>
      request<{ guest: CheckInGuest }>(`/events/${eventId}/checkin/qr`, {
        method: 'POST',
        body: JSON.stringify({ qrCode }),
      }),

    manualCheckIn: (eventId: string, guestId: string) =>
      request<{ guest: CheckInGuest }>(`/events/${eventId}/checkin/${guestId}`, { method: 'POST' }),

    undoCheckIn: (eventId: string, guestId: string) =>
      request<{ guest: CheckInGuest }>(`/events/${eventId}/checkin/${guestId}`, { method: 'DELETE' }),

    stats: (eventId: string) =>
      request<CheckInStats>(`/events/${eventId}/checkin/stats`),
  },

  eventPage: {
    getPublic: (slug: string) =>
      request<PublicEventPageData>(`/e/${slug}`),

    updateSettings: (
      eventId: string,
      data: {
        descriptionHtml?: string | null;
        bannerImageUrl?: string | null;
        virtualEventUrl?: string | null;
        virtualEventType?: string | null;
        mapEmbedUrl?: string | null;
        showGuestList?: boolean;
        slug?: string | null;
      },
    ) =>
      request<EventPageSettings>(`/events/${eventId}/page-settings`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
  },

  billing: {
    createCheckout: (eventId: string, promoCode?: string) =>
      request<CheckoutResult>(`/events/${eventId}/checkout`, {
        method: 'POST',
        body: JSON.stringify({ promoCode }),
      }),

    validatePromo: (eventId: string, code: string) =>
      request<PromoValidation>(`/events/${eventId}/checkout/validate-promo`, {
        method: 'POST',
        body: JSON.stringify({ code }),
      }),

    getBilling: (eventId: string) =>
      request<{ billing: EventBillingDetail | null }>(`/events/${eventId}/billing`),

    refund: (eventId: string) =>
      request<{ billing: EventBillingDetail }>(`/events/${eventId}/billing/refund`, {
        method: 'POST',
      }),
  },

  promoCodes: {
    create: (data: CreatePromoCodeData) =>
      request<PromoCodeItem>('/admin/promo-codes', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    list: () => request<PromoCodeItem[]>('/admin/promo-codes'),

    deactivate: (id: string) =>
      request<PromoCodeItem>(`/admin/promo-codes/${id}`, { method: 'DELETE' }),
  },

  branding: {
    get: () => request<Branding>('/account/branding'),
    update: (data: Partial<Omit<Branding, 'hostId'>>) =>
      request<Branding>('/account/branding', { method: 'PUT', body: JSON.stringify(data) }),
  },

  apiKeys: {
    list: () => request<ApiKeyData[]>('/account/api-keys'),
    create: (data: { name: string; permissions: string[] }) =>
      request<ApiKeyCreateResult>('/account/api-keys', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    revoke: (keyId: string) =>
      request<{ message: string }>(`/account/api-keys/${keyId}`, { method: 'DELETE' }),
  },

  images: {
    list: () => request<HostImageData[]>('/account/images'),
    upload: (data: { data: string; filename: string; mimeType: string }) =>
      request<HostImageData>('/account/images', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    delete: (imageId: string) =>
      request<{ message: string }>(`/account/images/${imageId}`, { method: 'DELETE' }),
  },

  admin: {
    stats: () => request<PlatformStats>('/admin/stats'),
    listHosts: (page = 1, limit = 20, search = '') =>
      request<PaginatedResponse<HostWithStats>>(`/admin/hosts?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`),
    getHost: (hostId: string) => request<HostDetail>(`/admin/hosts/${hostId}`),
    resetPassword: (hostId: string, password: string) =>
      request<{ success: boolean }>(`/admin/hosts/${hostId}/reset-password`, {
        method: 'POST', body: JSON.stringify({ password }),
      }),
    toggleActive: (hostId: string) =>
      request<{ host: HostWithStats }>(`/admin/hosts/${hostId}/toggle-active`, { method: 'POST' }),

    privacy: {
      listRequests: (page = 1, limit = 20) =>
        request<{ requests: DeletionRequestAdmin[]; total: number; page: number; limit: number; totalPages: number }>(
          `/admin/privacy/deletion-requests?page=${page}&limit=${limit}`,
        ),
      processDeletions: () =>
        request<ProcessDeletionsResult>('/admin/privacy/process-deletions', { method: 'POST' }),
    },

    analytics: (from?: string, to?: string) => {
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      const qs = params.toString();
      return request<PlatformAnalyticsData>(`/admin/analytics${qs ? `?${qs}` : ''}`);
    },

    // Feature Flags
    listFeatureFlags: () => request<FeatureFlag[]>('/admin/feature-flags'),
    createFeatureFlag: (data: { name: string; description?: string; isEnabled?: boolean; targetPlans?: string[] | null; metadata?: Record<string, unknown> | null }) =>
      request<FeatureFlag>('/admin/feature-flags', { method: 'POST', body: JSON.stringify(data) }),
    updateFeatureFlag: (id: string, data: Partial<{ name: string; description: string; isEnabled: boolean; targetPlans: string[] | null; metadata: Record<string, unknown> | null }>) =>
      request<FeatureFlag>(`/admin/feature-flags/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteFeatureFlag: (id: string) =>
      request<void>(`/admin/feature-flags/${id}`, { method: 'DELETE' }),

    // Templates (admin)
    listTemplates: () => request<AdminTemplate[]>('/admin/templates'),
    createTemplate: (data: { name: string; category: string; htmlLayout: string; previewImageUrl: string; isDefault?: boolean; isActive?: boolean }) =>
      request<AdminTemplate>('/admin/templates', { method: 'POST', body: JSON.stringify(data) }),
    updateTemplate: (id: string, data: Partial<{ name: string; category: string; htmlLayout: string; previewImageUrl: string; isDefault: boolean; isActive: boolean }>) =>
      request<AdminTemplate>(`/admin/templates/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deactivateTemplate: (id: string) =>
      request<void>(`/admin/templates/${id}`, { method: 'DELETE' }),

    // Abuse Reports
    listAbuseReports: (status?: string) =>
      request<AbuseReport[]>(`/admin/abuse-reports${status ? `?status=${status}` : ''}`),
    createAbuseReport: (data: { hostId?: string; eventId?: string; reportType: string; description?: string }) =>
      request<AbuseReport>('/admin/abuse-reports', { method: 'POST', body: JSON.stringify(data) }),
    resolveAbuseReport: (id: string, resolution: string) =>
      request<AbuseReport>(`/admin/abuse-reports/${id}/resolve`, { method: 'PUT', body: JSON.stringify({ resolution }) }),

    // Email Health
    getEmailHealth: (days = 30) =>
      request<EmailHealthResponse>(`/admin/email-health?days=${days}`),
    recordEmailMetrics: () =>
      request<EmailHealthMetric>('/admin/email-health/record', { method: 'POST' }),
  },

  privacy: {
    requestDeletion: (email: string) =>
      request<DeletionRequestResult>('/privacy/deletion-request', {
        method: 'POST',
        body: JSON.stringify({ email }),
      }),

    checkStatus: (id: string) =>
      request<DeletionRequestStatus>(`/privacy/deletion-request/${id}`),

    requestExport: (email: string) =>
      request<DataExportResult>('/privacy/data-export', {
        method: 'POST',
        body: JSON.stringify({ email }),
      }),

    downloadExport: (id: string) => `/api/privacy/export/${id}`,
  },

  account: {
    getProfile: () => request<HostProfileData>('/account/profile'),

    updateProfile: (data: { name?: string; email?: string }) =>
      request<{ id: string; email: string; name: string; plan: string }>('/account/profile', {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    changePassword: (data: { currentPassword: string; newPassword: string }) =>
      request<{ success: boolean }>('/account/change-password', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    getEmailUsage: () => request<EmailUsageSummary>('/account/email-usage'),

    getNotifications: () => request<NotificationPrefsData>('/account/notifications'),

    updateNotifications: (data: {
      emailOnRsvp?: boolean;
      emailOnReminder?: boolean;
      emailOnBilling?: boolean;
      emailDigest?: string;
    }) =>
      request<NotificationPrefsData>('/account/notifications', {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    getBillingHistory: () => request<BillingRecord[]>('/account/billing-history'),

    getRetentionPolicy: () =>
      request<DataRetentionPolicy>('/account/data-retention'),

    updateRetentionPolicy: (data: { retainGuestDataDays: number; autoDeleteEnabled: boolean }) =>
      request<DataRetentionPolicy>('/account/data-retention', {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
  },

  analytics: {
    getEventAnalytics: (eventId: string) =>
      request<EventAnalytics>(`/events/${eventId}/analytics`),
  },

  referral: {
    trackClick: (data: { source: string; referrer?: string; eventId?: string; ipHash?: string }) =>
      request<{ id: string }>('/referral/click', { method: 'POST', body: JSON.stringify(data) }),
  },
};
